import { useAuth } from '../../../contexts/AuthContext';

/**
 * Reward Speed Dial
 * Quick choice modal: Voucher or Referral
 *
 * Appears when user taps the Reward button in action bar or desktop top bar
 */
interface RewardSpeedDialProps {
  onClose: () => void;
  onSelectVoucher: () => void;
  onSelectReferral: () => void;
}

function RewardSpeedDial({ onClose, onSelectVoucher, onSelectReferral }: RewardSpeedDialProps) {
  const { hasPermission } = useAuth();

  const canCreateVoucher = hasPermission('vouchers', 'contribute');
  // Sharing an existing referral code is view-level (codes are auto-created).
  // Creating a voucher stays at contribute, so operators see Share only.
  const canShareReferral = hasPermission('referrals', 'view');

  // If user has no reward permissions, show friendly message
  if (!canCreateVoucher && !canShareReferral) {
    return (
      <div className="mobile-modal-overlay" onClick={onClose}>
        <div
          className="reward-speed-dial"
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed',
            bottom: '100px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'white',
            borderRadius: 'var(--radius-xl)',
            padding: '2rem 1.5rem',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
            width: 'calc(100% - 2rem)',
            maxWidth: '320px',
            textAlign: 'center',
            zIndex: 1100
          }}
        >
          <div style={{ fontSize: 'var(--text-4xl)', marginBottom: '1rem' }}>🎁</div>
          <div className="fw-600 mb-sm">
            Reward Access Required
          </div>
          <div className="text-sm text-muted mb-xl">
            You don&apos;t have permission to create vouchers or share referrals. Contact your administrator if you need access.
          </div>
          <button
            onClick={onClose}
            className="btn btn-primary w-full"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-modal-overlay" onClick={onClose}>
      <div
        className="reward-speed-dial"
        onClick={e => e.stopPropagation()}
        style={{
          position: 'fixed',
          bottom: '100px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'white',
          borderRadius: 'var(--radius-xl)',
          padding: '1rem',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          width: 'calc(100% - 2rem)',
          maxWidth: '320px',
          animation: 'slideUp 0.2s ease-out',
          zIndex: 1100
        }}
      >
        <div style={{
          textAlign: 'center',
          marginBottom: '1rem',
          fontSize: 'var(--text-base)',
          color: 'var(--color-text-light)'
        }}>
          What would you like to do?
        </div>

        <div className="flex gap-md">
          {/* Create Voucher */}
          {canCreateVoucher && (
            <button
              onClick={onSelectVoucher}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '1.25rem 1rem',
                background: 'linear-gradient(135deg, #f3e8ff 0%, #fce7f3 100%)',
                border: '2px solid var(--color-primary)',
                borderRadius: 'var(--radius-lg)',
                cursor: 'pointer'
              }}
            >
              <span style={{ fontSize: 'var(--text-5xl)' }}>🎟️</span>
              <span className="fw-600 text-primary text-sm">
                Voucher
              </span>
              <span className="text-xs text-muted">
                Create discount
              </span>
            </button>
          )}

          {/* Share Referral */}
          {canShareReferral && (
            <button
              onClick={onSelectReferral}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '1.25rem 1rem',
                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                border: '2px solid #10b981',
                borderRadius: 'var(--radius-lg)',
                cursor: 'pointer'
              }}
            >
              <span style={{ fontSize: 'var(--text-5xl)' }}>🔗</span>
              <span style={{ fontWeight: 600, color: 'var(--color-success)', fontSize: 'var(--text-base)' }}>
                Referral
              </span>
              <span className="text-xs text-muted">
                Share code
              </span>
            </button>
          )}
        </div>

        <button
          onClick={onClose}
          style={{
            width: '100%',
            marginTop: '0.75rem',
            padding: '0.75rem',
            background: 'transparent',
            border: 'none',
            color: 'var(--color-text-light)',
            cursor: 'pointer',
            fontSize: 'var(--text-base)'
          }}
        >
          Cancel
        </button>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default RewardSpeedDial;
